import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { useI18n } from '../i18n';
import { api } from '../api';
import { useRecaptcha } from '../hooks/useRecaptcha';

function VerdictSpecimen({ label, kind }) {
  return (
    <span className={`verdict-badge verdict-${kind}`}>
      <span className="opacity-60">▸</span>{label}
    </span>
  );
}

function Ticker() {
  const items = [
    'AVM range · 33301 · $612k–$684k',
    'Flood zone AE · verify FEMA',
    'Stale listing · 112 DOM · Miami Beach',
    'Absentee owner · LLC · 33141',
    'Permits pulled · 61 · Fort Lauderdale',
    'Price change · −5.8% · South Beach',
    'Insurance flag · post-2001 · verify',
    'Missingness · title chain · declared',
  ];
  const doubled = [...items, ...items];
  return (
    <div className="border-y hairline overflow-hidden">
      <div className="flex items-center gap-4 py-2.5">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold pl-6 md:pl-12 whitespace-nowrap flex items-center gap-2">
          <span className="h-1.5 w-1.5 bg-gold rounded-full blink" />
          <span>—</span>
        </div>
        <div className="overflow-hidden flex-1">
          <div className="ticker-track mono text-[11px] uppercase tracking-widest2 text-white/50 whitespace-nowrap">
            {doubled.map((it, i) => (
              <span key={i} className="mx-8 inline-flex items-center gap-2">
                <span className="text-gold/60">◆</span>{it}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ badge, title, price, unit, list, cta, ctaTo, dataId, gold }) {
  return (
    <div className={`border hairline bg-surface1 p-8 flex flex-col ${gold ? 'border-gold/40' : ''}`} data-testid={dataId}>
      <div className="mono text-[10px] uppercase tracking-widest2 text-gold">{badge}</div>
      <div className="serif text-3xl mt-3 leading-none">{title}</div>
      <div className="flex items-baseline gap-2 mt-6">
        <div className="serif text-5xl">{price}</div>
        <div className="mono text-[10px] uppercase tracking-widest2 text-white/50">{unit}</div>
      </div>
      <ul className="mt-6 space-y-2.5 text-sm text-white/80 flex-1">
        {list.map((li, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="text-gold mono mt-1 text-[10px]">▸</span>
            <span>{li}</span>
          </li>
        ))}
      </ul>
      <Link
        to={ctaTo}
        className={`mt-8 inline-flex items-center justify-center px-5 py-3 mono text-[11px] uppercase tracking-widest2 border ${gold ? 'border-gold text-gold hover:bg-gold hover:text-obsidian' : 'hairline text-white hover:border-gold/60 hover:text-gold'}`}
        style={{transitionProperty: 'color, background-color, border-color'}}
      >
        {cta} <span className="ml-2">→</span>
      </Link>
    </div>
  );
}

function TerritoryTable() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/territories/public')
      .then(r => setRows(r.data))
      .catch(err => {
        // Non-blocking: territory preview is a landing-page teaser; render empty on failure.
        console.warn('Territory preview fetch failed:', err);
      });
  }, []);
  return (
    <div className="border hairline bg-surface1 overflow-hidden" data-testid="territory-preview">
      <div className="grid grid-cols-12 mono text-[10px] uppercase tracking-widest2 text-white/40 border-b hairline">
        <div className="col-span-1 p-3">{t('radar.col.zip')}</div>
        <div className="col-span-4 p-3">{t('radar.col.name')}</div>
        <div className="col-span-2 p-3">{t('radar.col.signal')}</div>
        <div className="col-span-1 p-3">{t('radar.col.dom')}</div>
        <div className="col-span-1 p-3">{t('radar.col.change')}</div>
        <div className="col-span-1 p-3">{t('radar.col.flood')}</div>
        <div className="col-span-1 p-3 text-right">{t('radar.col.owners')}</div>
        <div className="col-span-1 p-3 text-right">{t('radar.col.opps')}</div>
      </div>
      {rows.map((r) => (
        <div key={r.id} className="grid grid-cols-12 mono text-xs border-b hairline last:border-0 hover:bg-surface2/60" style={{transitionProperty: 'background-color'}}>
          <div className="col-span-1 p-3 text-gold">{r.zip_code}</div>
          <div className="col-span-4 p-3 text-white">{r.name}</div>
          <div className="col-span-2 p-3 flex items-center gap-3">
            <span className="w-16 signal-bar"><span style={{width: `${r.signal_score}%`}}/></span>
            <span className="text-white/70">{r.signal_score}</span>
          </div>
          <div className="col-span-1 p-3 text-white/70">{r.median_dom}</div>
          <div className={`col-span-1 p-3 ${r.price_change_pct < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{r.price_change_pct > 0 ? '+' : ''}{r.price_change_pct}%</div>
          <div className="col-span-1 p-3">
            <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase ${r.flood_risk === 'high' ? 'text-red-400 border border-red-400/40' : r.flood_risk === 'moderate' ? 'text-amber-400 border border-amber-400/40' : 'text-emerald-400 border border-emerald-400/40'}`}>{r.flood_risk}</span>
          </div>
          <div className="col-span-1 p-3 text-right text-white/40 tracking-widest">{r.absentee_owners}</div>
          <div className="col-span-1 p-3 text-right text-white/40 tracking-widest">{r.opportunities}</div>
        </div>
      ))}
      <div className="p-4 flex items-center justify-between border-t hairline bg-surface2/40">
        <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">
          🔒 <span className="ml-1">Absentee + Opportunities unlocked with retainer</span>
        </div>
        <Link to="/intake" className="mono text-[10px] uppercase tracking-widest2 text-gold hover:text-white">Unlock →</Link>
      </div>
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  const executeRecaptcha = useRecaptcha();
  const [lead, setLead] = useState({ name: '', email: '', role: 'investor' });
  const [leadSent, setLeadSent] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      const recaptcha_token = await executeRecaptcha('lead_submit');
      await api.post('/leads', { ...lead, source: 'landing-inline', recaptcha_token });
      setLeadSent(true);
    } catch (err) {
      // Surface any submit failure to console + non-blocking UX (button stays enabled so user can retry).
      console.error('Lead submit failed:', err);
      alert(err?.response?.data?.detail || (lang === 'es'
        ? 'No pudimos registrar tu solicitud. Intenta de nuevo.'
        : 'We could not register your request. Please try again.'));
    }
  };

  return (
    <div>
      <Nav />
      <Ticker />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.pexels.com/photos/773842/pexels-photo-773842.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-20 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="mono text-[11px] uppercase tracking-widest2 text-gold mb-6">{t('hero.eyebrow')}</div>
            <h1 className="serif text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <span className="text-white">{t('hero.title.a')}</span><br/>
              <span className="text-gold italic">{t('hero.title.b')}</span>
            </h1>
            <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">{t('hero.subtitle')}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/intake"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white"
                style={{transitionProperty: 'background-color'}}
              >
                {t('hero.cta.primary')} <span>→</span>
              </Link>
              <Link
                to="/sample"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 px-6 py-3.5 border hairline text-white mono text-[11px] uppercase tracking-widest2 hover:border-gold hover:text-gold"
                style={{transitionProperty: 'color, border-color'}}
              >
                {t('hero.cta.secondary')}
              </Link>
            </div>
            <div className="mt-10 mono text-[10px] uppercase tracking-widest2 text-white/40 max-w-xl">
              — {t('hero.disclaimer')}
            </div>
          </div>

          <div className="lg:col-span-4 fade-up delay-2">
            <div className="border hairline bg-surface1/70 p-6">
              <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">Deal Verdict · SPECIMEN</div>
              <div className="serif text-2xl leading-tight text-white">168 Fiesta Way</div>
              <div className="mono text-xs text-white/50 mt-1">Fort Lauderdale · 33301</div>
              <div className="mt-6">
                <VerdictSpecimen label="NEGOTIATE" kind="negotiate" />
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3 mono text-xs">
                <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">AVM Low</div><div className="text-white mt-1">$612k</div></div>
                <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Median</div><div className="text-gold mt-1">$648k</div></div>
                <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">High</div><div className="text-white mt-1">$684k</div></div>
              </div>
              <div className="mt-6 pt-4 border-t hairline">
                <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">Risk stack</div>
                <div className="mt-2 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-white/70">Flood zone</span><span className="text-amber-400 mono text-xs">MODERATE</span></div>
                  <div className="flex justify-between"><span className="text-white/70">Insurance</span><span className="text-red-400 mono text-xs">HIGH</span></div>
                  <div className="flex justify-between"><span className="text-white/70">Permits</span><span className="text-emerald-400 mono text-xs">LOW</span></div>
                </div>
              </div>
              <div className="mt-4 mono text-[10px] uppercase tracking-widest2 text-white/40">Confidence · 74</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 01 · PROBLEM</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight text-white">{t('problem.title')}</h2>
            <p className="mt-6 text-white/60 max-w-lg">{t('problem.sub')}</p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { t: t('problem.a.title'), b: t('problem.a.body'), n: '01' },
              { t: t('problem.b.title'), b: t('problem.b.body'), n: '02' },
              { t: t('problem.c.title'), b: t('problem.c.body'), n: '03' },
            ].map((c, i) => (
              <div key={i} className="border hairline bg-surface1 p-6 flex flex-col gap-4 hover:border-gold/40" style={{transitionProperty: 'border-color'}}>
                <div className="mono text-[10px] uppercase tracking-widest2 text-gold">{c.n}</div>
                <div className="serif text-xl text-white">{c.t}</div>
                <div className="text-sm text-white/60 leading-relaxed">{c.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="border-t hairline relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1644088379091-d574269d422f" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 02 · METHOD</div>
            <div className="mono text-[11px] uppercase tracking-widest2 text-white/50">{t('method.eyebrow')}</div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="serif text-4xl md:text-5xl leading-tight text-white">{t('method.title')}</h2>
            <p className="mt-6 text-white/70 text-lg max-w-3xl leading-relaxed">{t('method.body')}</p>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {['proceed','negotiate','hold','reject'].map((k) => (
                <div key={k} className={`border hairline p-4 bg-surface1/60`}>
                  <VerdictSpecimen label={t(`verdict.${k}`).toUpperCase()} kind={k} />
                  <div className="mt-3 mono text-[10px] uppercase tracking-widest2 text-white/40">confidence 0–100</div>
                </div>
              ))}
            </div>
            <Link to="/methodology" className="inline-flex mt-8 items-center gap-2 mono text-[11px] uppercase tracking-widest2 text-gold hover:text-white">
              Read the full method →
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 03 · PRODUCTS</div>
              <h2 className="serif text-4xl md:text-5xl leading-tight text-white">{t('products.title')}</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-white/60">{t('products.sub')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ProductCard
              dataId="product-deal"
              badge={t('product.a.badge')}
              title={t('product.a.title')}
              price={t('product.a.price')}
              unit={t('product.a.unit')}
              list={t('product.a.list')}
              cta={t('product.a.cta')}
              ctaTo="/intake"
              gold
            />
            <ProductCard
              dataId="product-radar"
              badge={t('product.b.badge')}
              title={t('product.b.title')}
              price={t('product.b.price')}
              unit={t('product.b.unit')}
              list={t('product.b.list')}
              cta={t('product.b.cta')}
              ctaTo="/intake"
            />
            <ProductCard
              dataId="product-desk"
              badge={t('product.c.badge')}
              title={t('product.c.title')}
              price={t('product.c.price')}
              unit={t('product.c.unit')}
              list={t('product.c.list')}
              cta={t('product.c.cta')}
              ctaTo="/intake"
            />
          </div>
        </div>
      </section>

      {/* RADAR PREVIEW */}
      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 04 · RADAR</div>
              <h2 className="serif text-4xl md:text-5xl leading-tight text-white max-w-2xl">{t('radar.title')}</h2>
            </div>
            <p className="text-white/60 max-w-md">{t('radar.sub')}</p>
          </div>
          <TerritoryTable />
        </div>
      </section>

      {/* SAMPLE ANATOMY */}
      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
          <div className="mb-14">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 05 · SAMPLE</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight text-white max-w-2xl">{t('sample.title')}</h2>
            <p className="mt-4 text-white/60 max-w-2xl">{t('sample.sub')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 border hairline bg-surface1 p-6">
              <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">SOURCE LEDGER · Excerpt</div>
              <table className="w-full mt-4 mono text-xs">
                <thead>
                  <tr className="text-white/40 text-[10px] uppercase tracking-widest2 border-b hairline">
                    <th className="text-left py-2 pr-3">Field</th>
                    <th className="text-left py-2 pr-3">Value</th>
                    <th className="text-left py-2 pr-3">Source</th>
                    <th className="text-right py-2 pr-3">Conf.</th>
                    <th className="text-right py-2">Miss.</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {[
                    ['Asking price', '$680,000', 'user_input', 100, 'none'],
                    ['Year built', '1962', 'public_record_placeholder', 85, 'partial'],
                    ['Flood zone', 'AE (verify)', 'model_hypothesis', 60, 'partial'],
                    ['Living area (sqft)', '2,340', 'public_record_placeholder', 80, 'none'],
                    ['Owner LLC', 'unknown', 'not_available', 0, 'full'],
                    ['Comparable sales', 'range only', 'model_hypothesis', 55, 'partial'],
                    ['Insurance premium est.', '$8,400–$14,200 / yr', 'model_hypothesis', 50, 'partial'],
                  ].map((row, i) => (
                    <tr key={i} className="border-b hairline last:border-0">
                      <td className="py-2 pr-3 text-white">{row[0]}</td>
                      <td className="py-2 pr-3">{row[1]}</td>
                      <td className="py-2 pr-3 text-white/50">{row[2]}</td>
                      <td className="py-2 pr-3 text-right"><span className={row[3] >= 70 ? 'text-emerald-400' : row[3] >= 40 ? 'text-amber-400' : 'text-red-400'}>{row[3]}</span></td>
                      <td className="py-2 text-right">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase ${row[4] === 'none' ? 'text-emerald-400 border border-emerald-400/40' : row[4] === 'partial' ? 'text-amber-400 border border-amber-400/40' : 'text-red-400 border border-red-400/40'}`}>
                          {row[4]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to="/sample" data-testid="sample-cta" className="mt-6 inline-flex mono text-[11px] uppercase tracking-widest2 text-gold hover:text-white">
                See the full report →
              </Link>
            </div>

            <div className="lg:col-span-4 flex flex-col gap-4">
              <div className="border hairline bg-surface1 p-6">
                <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">RED FLAGS</div>
                <ul className="mt-4 space-y-2 text-sm text-white/80">
                  <li className="flex gap-3"><span className="text-red-400 mono">◆</span> Insurance premium projected to exceed rental yield</li>
                  <li className="flex gap-3"><span className="text-red-400 mono">◆</span> Title chain missing 1998–2004</li>
                  <li className="flex gap-3"><span className="text-amber-400 mono">◆</span> Days on market above ZIP median by 41%</li>
                </ul>
              </div>
              <div className="border hairline bg-surface1 p-6">
                <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">NEGOTIATION LEVERS</div>
                <ul className="mt-4 space-y-2 text-sm text-white/80">
                  <li className="flex gap-3"><span className="text-gold mono">▸</span> Anchor at AVM low + insurance delta</li>
                  <li className="flex gap-3"><span className="text-gold mono">▸</span> Request seller-paid inspection credit</li>
                  <li className="flex gap-3"><span className="text-gold mono">▸</span> Time-lock at 21 days with 2% earnest</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
          <div className="mb-14">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 06 · COMPLIANCE</div>
            <h2 className="serif text-4xl md:text-5xl leading-tight text-white max-w-2xl">{t('compliance.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border hairline bg-surface1 p-8">
              <div className="mono text-[11px] uppercase tracking-widest2 text-emerald-400">{t('compliance.is')}</div>
              <ul className="mt-6 space-y-3 text-white/80">
                {['compliance.is.a','compliance.is.b','compliance.is.c','compliance.is.d'].map((k) => (
                  <li key={k} className="flex gap-3"><span className="text-emerald-400 mono mt-1">+</span>{t(k)}</li>
                ))}
              </ul>
            </div>
            <div className="border hairline bg-surface1 p-8">
              <div className="mono text-[11px] uppercase tracking-widest2 text-red-400">{t('compliance.isnot')}</div>
              <ul className="mt-6 space-y-3 text-white/80">
                {['compliance.isnot.a','compliance.isnot.b','compliance.isnot.c','compliance.isnot.d'].map((k) => (
                  <li key={k} className="flex gap-3"><span className="text-red-400 mono mt-1">−</span>{t(k)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT CTA */}
      <section className="border-t hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold mb-4">§ 07 · PILOT</div>
            <h2 className="serif text-5xl md:text-6xl leading-tight">{t('pilot.title')}</h2>
            <p className="mt-6 text-white/70 text-lg max-w-2xl">{t('pilot.body')}</p>
            <Link to="/intake" data-testid="pilot-cta" className="mt-8 inline-flex items-center gap-3 px-6 py-3.5 bg-gold text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-white" style={{transitionProperty: 'background-color'}}>
              {t('pilot.cta')} <span>→</span>
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="border hairline bg-surface1 p-8">
              <div className="mono text-[10px] uppercase tracking-widest2 text-white/40 mb-4">EARLY ACCESS</div>
              {leadSent ? (
                <div className="text-emerald-400 mono text-sm">{t('intake.success')}</div>
              ) : (
                <form onSubmit={submitLead} className="space-y-3" data-testid="early-access-form">
                  <input value={lead.name} onChange={e=>setLead({...lead, name: e.target.value})} required placeholder={t('intake.field.name')} className="w-full bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none" style={{transitionProperty: 'border-color'}} data-testid="lead-name" />
                  <input value={lead.email} onChange={e=>setLead({...lead, email: e.target.value})} type="email" required placeholder={t('intake.field.email')} className="w-full bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white placeholder-white/30 focus:border-gold outline-none" style={{transitionProperty: 'border-color'}} data-testid="lead-email" />
                  <select value={lead.role} onChange={e=>setLead({...lead, role: e.target.value})} className="w-full bg-obsidian border hairline px-3 py-2.5 mono text-sm text-white focus:border-gold outline-none" style={{transitionProperty: 'border-color'}} data-testid="lead-role">
                    <option value="investor">Investor</option>
                    <option value="broker">Broker / Agent</option>
                    <option value="fund">Fund / Lender</option>
                    <option value="buyer">Buyer</option>
                    <option value="other">Other</option>
                  </select>
                  <button type="submit" className="w-full py-3 bg-white text-obsidian mono text-[11px] uppercase tracking-widest2 hover:bg-gold" style={{transitionProperty: 'background-color'}} data-testid="lead-submit">
                    Request Access →
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <DisclaimerBar />
      <Footer />
    </div>
  );
}
